import { modal } from '@codeleap/modals'
import { AvatarInfo, View, Button, Icon, Text, Touchable, UserDetailsModal } from '@/components'
import { ChatMember, isMemberModerator, isMemberOwner } from '@packages/chat'
import { StaticImageData } from 'next/image'
import { createStyles } from '@codeleap/styles'
import { Chat, textVariant } from '@/config'
import { alert } from '@codeleap/modals'
import { appStatusStore } from '@/stores'
import { Channel } from 'stream-chat'

export type MemberOptionsModalProps = {
  member: ChatMember
  isAdmin?: boolean
  channel: Channel
  onRemoveSuccess?: () => void
}

export const MemberOptionsModal = modal<MemberOptionsModalProps>({ id: 'report-modal' }).content((props) => {
  const { member, isAdmin, channel, onRemoveSuccess } = props
  const canModerate = isAdmin && !isMemberOwner(member)

  const isModerator = isMemberModerator(member)

  function onInfoPress(member: ChatMember) {
    UserDetailsModal.open({
      user: member.user,
    })
    MemberOptionsModal.close()
  }

  async function onRemovePress(member: ChatMember) {
    appStatusStore.set('loading')
    Chat.Services.removeMember({
      channelId: channel.id,
      member: member.user,
      onSuccess: () => {
        MemberOptionsModal.close()
        onRemoveSuccess?.()
        appStatusStore.set('done')
      },
      onError: () => {
        appStatusStore.set('idle')
      },
    })
  }

  const onRemoveModeratorPress = async (member: ChatMember) => {
    appStatusStore.set('loading')
    MemberOptionsModal.close()
    Chat.Services.removeModerator({
      channelId: channel.id,
      member,
    }).finally(() => {
      appStatusStore.set('idle')
    })
  }

  const onMakeModeratorPress = async (member: ChatMember) => {
    appStatusStore.set('loading')
    MemberOptionsModal.close()
    Chat.Services.addModerator({
      channelId: channel.id,
      member,
    }).finally(() => appStatusStore.set('idle'))
  }

  return (
    <View style={['column', 'paddingTop:1']}>
      <View style={styles.itemWrapper}>
        <AvatarInfo title={member.user.name} avatar={member.user.image as unknown as StaticImageData} />
      </View>

      <Item
        icon='info-circle'
        text={`Member info`}
        onPress={() => onInfoPress?.(member)}
      />

      {canModerate && (
        <Item
          icon={isModerator ? 'user-down-02' : 'user-up-02'}
          text={isModerator ? 'Remove admin access' : 'Make group admin'}
          onPress={() => {
            if (isModerator) {
              alert.warn({
                title: 'Remove admin access',
                body: `Are you sure you want to remove admin access from this member?`,
                options: [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Remove', style: 'destructive', onPress: () => onRemoveModeratorPress?.(member) },
                ],
              })
            } else {
              alert.warn({
                title: 'Make member group admin',
                body: `Are you sure you want to make this member a group admin?`,
                options: [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Make admin', style: 'default', onPress: () => onMakeModeratorPress?.(member) },
                ],
              })
            }
          }}
        />
      )}

      {canModerate && (<Item
        icon='close-circle-line'
        text={`Remove from group`}
        variant='error'
        onPress={() => {
          alert.warn({
            title: 'Remove Member',
            body: 'Are you sure you want to remove this member from the group?',
            options: [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Remove', style: 'destructive', onPress: () => onRemovePress(member) },
            ],
          })
        }}
      />
      )}

      <Button
        debugName='MemberOptionsModal:cancel'
        text='Cancel'
        style={['neutralSecondary', 'margin:3']}
        onPress={props.toggle}
      />
    </View>
  )
}).props({
  showClose: false,
  style: ['centered', 'box:responsiveness', 'box:noPadding'],
})

function Item({ text, icon, onPress, variant = 'neutral' }) {
  const isError = variant === 'error'
  return (
    <Touchable debugName={`MemberOptionsModal:${text}`} onPress={onPress}>
      <View style={styles.itemWrapper}>
        <Icon style={[styles.icon, isError && styles.iconError]} name={icon} />
        <Text style={[styles.text, isError && styles.textError]} text={text} />
      </View>
    </Touchable>
  )
}

const styles = createStyles(theme => ({
  itemWrapper: {
    ...theme.presets.fullWidth,
    ...theme.spacing.paddingHorizontal(3),
    ...theme.spacing.paddingVertical(2),
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.presets.justifyStart,
    ...theme.border({ color: 'neutralSolid300', width: 1, directions: ['bottom'] }),
  },
  icon: {
    marginRight: theme.spacing.value(1),
    color: theme.baseColors.neutralSolid600,
    ...theme.sized(3),
  },
  iconError: {
    color: theme.baseColors.redSolid500,
  },
  text: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingHighlighted,
  },
  textError: {
    color: theme.baseColors.redSolid500,
  },
}))
